import { MODE_NAMES } from './logic/challenges';
import type { GameMode, GameSnapshot } from './logic/types';

export const DECORATIONS = [
  {
    id: 'flowers',
    name: 'Moonflower border',
    cost: 3,
    detail: 'Ivory flowers that catch the moonlight.',
  },
  {
    id: 'pots',
    name: 'Terracotta collection',
    cost: 6,
    detail: 'Hand-thrown pots, brimming with violet blooms.',
  },
  {
    id: 'path',
    name: 'Stepping-stone walk',
    cost: 10,
    detail: 'A winding path through your little sanctuary.',
  },
  {
    id: 'lanterns',
    name: 'Evening lanterns',
    cost: 15,
    detail: 'A warm welcome at the garden gate.',
  },
  {
    id: 'fountain',
    name: 'Lily fountain',
    cost: 20,
    detail: 'A quiet pool beneath a silver ribbon of water.',
  },
  {
    id: 'arbor',
    name: 'The rose arbor',
    cost: 25,
    detail: 'The finishing touch: climbing roses under the stars.',
  },
] as const;
export interface RecordBook {
  score: number;
  rows: number;
  streak: number;
  blooms: number;
  rounds: number;
}
export interface Progress {
  seeds: number;
  earned: number;
  owned: string[];
  equipped: string[];
  solved: string[];
  records: Partial<Record<GameMode, RecordBook>>;
}
const KEY = 'sage-stack.garden-v1';
const count = (v: unknown): number =>
  typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
export const emptyRecord = (): RecordBook => ({
  score: 0,
  rows: 0,
  streak: 0,
  blooms: 0,
  rounds: 0,
});
const empty = (): Progress => ({
  seeds: 0,
  earned: 0,
  owned: [],
  equipped: [],
  solved: [],
  records: {},
});
export function readProgress(): Progress {
  try {
    const data = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (!data || typeof data !== 'object') return empty();
    const ids = new Set<string>(DECORATIONS.map((d) => d.id));
    const strings = (v: unknown): string[] =>
      Array.isArray(v) ? [...new Set(v.filter((x): x is string => typeof x === 'string'))] : [];
    const owned = strings(data.owned).filter((id) => ids.has(id));
    const records: Progress['records'] = {};
    for (const mode of Object.keys(MODE_NAMES) as GameMode[]) {
      const r = data.records?.[mode];
      if (r && typeof r === 'object')
        records[mode] = {
          score: count(r.score),
          rows: count(r.rows),
          streak: count(r.streak),
          blooms: count(r.blooms),
          rounds: count(r.rounds),
        };
    }
    return {
      seeds: count(data.seeds),
      earned: count(data.earned),
      owned,
      equipped: strings(data.equipped).filter((id) => owned.includes(id)),
      solved: strings(data.solved).filter((id) =>
        /^(puzzle-\d+|daily-\d{4}-\d{2}-\d{2})$/.test(id),
      ),
      records,
    };
  } catch {
    return empty();
  }
}
export function saveProgress(progress: Progress): boolean {
  try {
    localStorage.setItem(KEY, JSON.stringify(progress));
    return true;
  } catch {
    return false;
  }
}
export function completeRound(
  progress: Progress,
  s: GameSnapshot,
): { seeds: number; newBest: boolean; firstSolve: boolean } {
  const previous = progress.records[s.mode] ?? emptyRecord();
  const newBest = s.score > previous.score;
  progress.records[s.mode] = {
    score: Math.max(previous.score, s.score),
    rows: Math.max(previous.rows, s.lines),
    streak: Math.max(previous.streak, s.bestBloomStreak),
    blooms: Math.max(previous.blooms, s.fullBlooms),
    rounds: previous.rounds + 1,
  };
  const firstSolve =
    s.endReason === 'challenge-complete' &&
    !!s.challengeId &&
    !progress.solved.includes(s.challengeId);
  if (firstSolve) progress.solved.push(s.challengeId!);
  const seeds =
    s.mode === 'puzzle-garden'
      ? firstSolve
        ? 5
        : 0
      : Math.min(
          30,
          (s.plantedPieces > 0 ? 1 : 0) +
            Math.floor(s.lines / 3) +
            s.fullBlooms * 2 +
            (firstSolve ? 8 : 0),
        );
  progress.seeds += seeds;
  progress.earned += seeds;
  return { seeds, newBest, firstSolve };
}
export function purchase(progress: Progress, id: string): boolean {
  const item = DECORATIONS.find((d) => d.id === id);
  if (!item || progress.owned.includes(id) || progress.seeds < item.cost) return false;
  progress.seeds -= item.cost;
  progress.owned.push(id);
  progress.equipped.push(id);
  return true;
}
